import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SiteHeader from "@/components/SiteHeader";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { MapPin, ArrowRight, Truck, ThumbsUp, ThumbsDown, Minus, Loader2, SlidersHorizontal, RotateCcw, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT = {
  good: { label: "Good lane", cls: "bg-emerald-100 text-emerald-700", border: "border-l-emerald-500", Icon: ThumbsUp },
  bad: { label: "Avoid", cls: "bg-red-100 text-red-700", border: "border-l-red-500", Icon: ThumbsDown },
  ok: { label: "Fair", cls: "bg-amber-100 text-amber-700", border: "border-l-amber-500", Icon: Minus },
};

const SORTS = [
  { id: "rpm_desc", label: "$/mile ↓" },
  { id: "rpm_asc", label: "$/mile ↑" },
  { id: "dh_asc", label: "Deadhead ↑" },
  { id: "rate_desc", label: "Total rate ↓" },
];

const money = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const DEFAULTS = { minRpm: 0, maxDeadhead: 250, sort: "rpm_desc" };

export default function LoadBoard() {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [active, setActive] = useState(null);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minRpm, setMinRpm] = useState(DEFAULTS.minRpm);
  const [maxDeadhead, setMaxDeadhead] = useState(DEFAULTS.maxDeadhead);
  const [sort, setSort] = useState(DEFAULTS.sort);
  const [booked, setBooked] = useState({});

  useEffect(() => {
    axios.get(`${API}/loadboard/equipment`).then(({ data }) => {
      setEquipment(data);
      setActive(data[0]?.id);
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    setBooked({});
    axios.get(`${API}/loadboard/${active}`).then(({ data }) => {
      setLoads(data);
      setLoading(false);
    });
  }, [active]);

  const visible = useMemo(() => {
    let out = loads.filter((l) => l.rpm >= minRpm && l.deadhead_miles <= maxDeadhead);
    const cmp = {
      rpm_desc: (a, b) => b.rpm - a.rpm,
      rpm_asc: (a, b) => a.rpm - b.rpm,
      dh_asc: (a, b) => a.deadhead_miles - b.deadhead_miles,
      rate_desc: (a, b) => b.rate - a.rate,
    }[sort];
    return [...out].sort(cmp);
  }, [loads, minRpm, maxDeadhead, sort]);

  const reset = () => {
    setMinRpm(DEFAULTS.minRpm);
    setMaxDeadhead(DEFAULTS.maxDeadhead);
    setSort(DEFAULTS.sort);
  };

  const bookLoad = (l) => setBooked((b) => ({ ...b, [l.id]: true }));

  const eqLabel = equipment.find((e) => e.id === active)?.label || active;
  const askCoach = (l) => {
    const q = `Should I take this ${eqLabel} load? ${l.origin} → ${l.destination}, ${l.miles} loaded miles plus ${l.deadhead_miles} deadhead, total $${l.rate} ($${l.rpm.toFixed(2)}/mile), broker ${l.broker}.`;
    navigate("/coach", { state: { ask: q } });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-6">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Simulated load board · practice mode</div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900">Learn to read the board before you're live</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          Pick your equipment and study 20 realistic loads — a mix of profitable lanes and traps. <span className="font-semibold text-slate-900">Book a load to see how it grades — good, fair, or a trap — and learn why</span> before you're out there for real. Rates modeled on market data (DAT, Trucksmarter). Not live freight.
        </p>
      </section>

      {/* Equipment tabs */}
      <section className="max-w-7xl mx-auto px-6 sticky top-16 z-20 bg-slate-50/90 backdrop-blur py-3">
        <div className="flex gap-2 flex-wrap">
          {equipment.map((e) => (
            <button
              key={e.id}
              onClick={() => setActive(e.id)}
              data-testid={`equipment-tab-${e.id}`}
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-bold transition-colors ${
                active === e.id ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200 hover:border-brand"
              }`}
            >
              <Truck className="w-4 h-4" /> {e.label}
            </button>
          ))}
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 pt-4">
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="loadboard-filters">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
              <SlidersHorizontal className="w-4 h-4 text-brand" /> Filters
            </div>
            <button onClick={reset} data-testid="filter-reset" className="inline-flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-slate-900">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-600 font-medium">Minimum rate</span>
                <span className="font-mono tabular font-bold text-slate-900" data-testid="filter-minrpm-value">${minRpm.toFixed(2)}/mi</span>
              </div>
              <Slider data-testid="filter-minrpm" min={0} max={3} step={0.05} value={[minRpm]} onValueChange={(v) => setMinRpm(v[0])} className="[&_[role=slider]]:bg-brand [&>span>span]:bg-brand" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-600 font-medium">Max deadhead</span>
                <span className="font-mono tabular font-bold text-slate-900" data-testid="filter-deadhead-value">{maxDeadhead} mi</span>
              </div>
              <Slider data-testid="filter-deadhead" min={0} max={250} step={10} value={[maxDeadhead]} onValueChange={(v) => setMaxDeadhead(v[0])} className="[&_[role=slider]]:bg-brand [&>span>span]:bg-brand" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-5">
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">Sort</span>
              {SORTS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSort(s.id)}
                  data-testid={`sort-${s.id}`}
                  className={`px-3 h-8 rounded-full text-xs font-bold transition-colors ${sort === s.id ? "bg-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="text-sm text-slate-500 mt-3" data-testid="loadboard-count">
          Showing <span className="font-bold text-slate-900">{visible.length}</span> of {loads.length} loads
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="py-20 text-center" data-testid="loadboard-empty">
            <p className="text-slate-500 mb-4">No loads match your filters. Try loosening them.</p>
            <Button onClick={reset} variant="outline" className="rounded-full border-slate-300 font-bold"><RotateCcw className="w-4 h-4 mr-2" />Reset filters</Button>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div key={active + sort} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visible.map((l) => {
                const v = VERDICT[l.verdict] ?? VERDICT.ok;
                const isBooked = booked[l.id];
                return (
                  <div key={l.id} data-testid={`load-card-${l.id}`} className={`bg-white border border-slate-200 border-l-4 ${isBooked ? v.border : "border-l-slate-200"} rounded-md p-5`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-500">
                        <Truck className="w-3 h-3" /> {eqLabel}
                      </span>
                      <span className="font-mono text-lg font-bold tabular text-slate-900">${l.rpm.toFixed(2)}<span className="text-xs text-slate-400">/mi</span></span>
                    </div>

                    <div className="flex items-center gap-2 text-slate-900 font-semibold">
                      <MapPin className="w-4 h-4 text-brand shrink-0" /> {l.origin}
                    </div>
                    <div className="flex items-center gap-2 text-slate-900 font-semibold mt-1">
                      <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" /> {l.destination}
                    </div>

                    <div className="grid grid-cols-2 gap-y-2 gap-x-3 mt-4 text-sm">
                      <Stat label="Rate" value={money(l.rate)} strong />
                      <Stat label="Miles" value={`${l.miles} mi`} />
                      <Stat label="Deadhead" value={`${l.deadhead_miles} mi`} warn={l.deadhead_miles >= 80} />
                      <Stat label="Est. fuel" value={money(l.est_fuel_cost)} />
                      <Stat label="Weight" value={`${l.weight_lbs.toLocaleString()} lb`} />
                      <Stat label="Broker" value={l.broker} />
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-100">
                      {isBooked ? (
                        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`load-feedback-${l.id}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${v.cls}`}>
                              <v.Icon className="w-3 h-3" /> {v.label}
                            </span>
                            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">Booking graded</span>
                          </div>
                          <span className="text-xs text-slate-600 leading-relaxed">{l.reason}</span>
                          <button
                            onClick={() => askCoach(l)}
                            data-testid={`ask-coach-${l.id}`}
                            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-full text-xs font-bold text-brand bg-orange-50 hover:bg-orange-100 transition-colors"
                          >
                            <Sparkles className="w-3.5 h-3.5" /> Ask coach to go deeper
                          </button>
                        </motion.div>
                      ) : (
                        <button
                          onClick={() => bookLoad(l)}
                          data-testid={`book-load-${l.id}`}
                          className="w-full inline-flex items-center justify-center gap-1.5 h-10 rounded-full text-sm font-bold text-white bg-brand hover:bg-brand-hover transition-colors"
                        >
                          Book this load
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}
      </section>
    </div>
  );
}

const Stat = ({ label, value, strong, warn }) => (
  <div>
    <div className="text-[10px] tracking-[0.1em] uppercase text-slate-400 font-bold">{label}</div>
    <div className={`font-mono tabular ${strong ? "font-bold text-slate-900" : warn ? "text-red-600" : "text-slate-700"}`}>{value}</div>
  </div>
);
